import type { PreMeetingPrepResponseStreamJson } from "@performyard/service-contracts";

import type { PreMeetingPrepGoalItem } from "../types";

import { PRE_MEETING_PREP_CONFIG } from "../config";

const { sectionTitleMap } = PRE_MEETING_PREP_CONFIG;

export const isNoDataPlaceholder = (value: string): boolean => {
	const v = value.trim().toLowerCase();
	return (
		v === "no data provided" ||
		/^no (recent |survey )?(review |feedback |survey )?data (available|provided)/i.test(v)
	);
};

export const formatPrepAsPlainText = (
	data: PreMeetingPrepResponseStreamJson,
	goals?: {
		overdue: PreMeetingPrepGoalItem[];
		upcomingDueDate: PreMeetingPrepGoalItem[];
		outstandingUpdates: PreMeetingPrepGoalItem[];
	}
): string => {
	const lines: string[] = [];

	lines.push(`Pre-Meeting Prep: ${data.meetingName}`);
	lines.push(`Meeting Date: ${data.meetingDate}`);
	lines.push("");

	// Executive Summary
	if (data.executiveSummary) {
		lines.push(`--- ${sectionTitleMap.executiveSummary} ---`);
		lines.push(data.executiveSummary);
		lines.push("");
	}

	// Last Three Meetings Recap
	if (data.lastThreeMeetingsRecap?.length) {
		lines.push(`--- ${sectionTitleMap.lastThreeMeetingsRecap} ---`);
		for (const recap of data.lastThreeMeetingsRecap) {
			lines.push(`• ${recap}`);
		}
		lines.push("");
	}

	// Suggested Agenda Items
	if (data.suggestedAgendaItems?.length) {
		lines.push(`--- ${sectionTitleMap.suggestedAgendaItems} ---`);
		for (const item of data.suggestedAgendaItems) {
			lines.push(`• ${item.topic}: ${item.reason}`);
		}
		lines.push("");
	}

	// Goals for Discussion
	if (goals) {
		const allGoals = [...goals.overdue, ...goals.upcomingDueDate, ...goals.outstandingUpdates];
		if (allGoals.length > 0) {
			lines.push("--- Goals for Discussion ---");
			if (goals.overdue.length > 0) {
				lines.push("Overdue:");
				for (const g of goals.overdue) {
					lines.push(`  • ${g.goalName} (Due: ${g.dueDate})`);
				}
			}
			if (goals.outstandingUpdates.length > 0) {
				lines.push("At Risk:");
				for (const g of goals.outstandingUpdates) {
					lines.push(`  • ${g.goalName}`);
				}
			}
			if (goals.upcomingDueDate.length > 0) {
				lines.push("Upcoming:");
				for (const g of goals.upcomingDueDate) {
					lines.push(`  • ${g.goalName} (Due: ${g.dueDate})`);
				}
			}
			lines.push("");
		}
	}

	// Recent Feedback
	if (data.recentFeedback?.length) {
		const filtered = data.recentFeedback.filter((item) => !isNoDataPlaceholder(item));
		if (filtered.length) {
			lines.push(`--- ${sectionTitleMap.recentFeedback} ---`);
			for (const item of filtered) {
				lines.push(`• ${item}`);
			}
			lines.push("");
		}
	}

	// Recent Reviews
	if (data.recentReviews && !isNoDataPlaceholder(data.recentReviews)) {
		lines.push(`--- ${sectionTitleMap.recentReviews} ---`);
		lines.push(data.recentReviews);
		lines.push("");
	}

	// Survey Insights
	if (data.surveyInsights && !isNoDataPlaceholder(data.surveyInsights)) {
		lines.push(`--- ${sectionTitleMap.surveyInsights} ---`);
		lines.push(data.surveyInsights);
		lines.push("");
	}

	return lines.join("\n");
};
